import { useEffect, useState } from "react";
import { getFirestore, collection, getDocs, addDoc, deleteDoc, doc, updateDoc } from "firebase/firestore";
import { useLocation } from "react-router-dom";
import app from "./firebase";
import styles from "./VendorsDashboard.module.css";

const db = getFirestore(app);

export default function VendorsDashboard() {
  const [vendors, setVendors] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [editVendor, setEditVendor] = useState(null);
  const [form, setForm] = useState({ 
    name: "", 
    contact: "", 
    email: "", 
    phone: "",
    address: "",
    website: ""
  });
  const location = useLocation();

  const fetchData = async () => {
    setLoading(true);
    try {
      const [vendorSnap, itemSnap] = await Promise.all([
        getDocs(collection(db, "vendors")),
        getDocs(collection(db, "items")),
      ]);
      setVendors(vendorSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setItems(itemSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { 
    fetchData(); 
  }, []);

  // Handle highlighting from search - triggers on any highlight event
  useEffect(() => {
    if (location.state?.highlightId && !loading && vendors.length > 0) {
      const scrollToElement = () => {
        const element = document.getElementById(`vendor-${location.state.highlightId}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          element.classList.add(styles.highlighted);
          setTimeout(() => {
            element.classList.remove(styles.highlighted);
          }, 3000);
          return true;
        }
        return false;
      };

      // Try immediately first
      if (!scrollToElement()) {
        // If element not found, wait a bit for DOM to update
        const timer = setTimeout(() => {
          scrollToElement();
        }, 100);
        return () => clearTimeout(timer);
      }
    }
  }, [location.state, loading, vendors]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name) return;
    await addDoc(collection(db, "vendors"), {
      name: form.name,
      contact: form.contact || "",
      email: form.email || "",
      phone: form.phone || "",
      address: form.address || "",
      website: form.website || "",
      createdAt: new Date()
    });
    setForm({ name: "", contact: "", email: "", phone: "", address: "", website: "" });
    setShowAdd(false);
    fetchData();
  };

  const handleDelete = async (vendorId) => {
    const itemCount = items.filter(item => item.vendorId === vendorId).length;
    if (itemCount > 0) {
      if (!window.confirm(`This vendor has ${itemCount} items. Are you sure you want to delete it? This will also affect those items.`)) {
        return;
      }
    } else {
      if (!window.confirm("Delete this vendor?")) return;
    }
    await deleteDoc(doc(db, "vendors", vendorId));
    fetchData();
  };

  const handleEdit = (vendor) => {
    setEditVendor(vendor);
    setForm({ 
      name: vendor.name,
      contact: vendor.contact || "",
      email: vendor.email || "",
      phone: vendor.phone || "",
      address: vendor.address || "",
      website: vendor.website || ""
    });
    setShowEdit(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!form.name) return;
    await updateDoc(doc(db, "vendors", editVendor.id), {
      name: form.name,
      contact: form.contact || "",
      email: form.email || "",
      phone: form.phone || "",
      address: form.address || "",
      website: form.website || "",
      lastUpdated: new Date()
    });
    setForm({ name: "", contact: "", email: "", phone: "", address: "", website: "" });
    setShowEdit(false);
    setEditVendor(null);
    fetchData();
  };

  const getVendorItems = (vendorId) => {
    return items.filter(item => item.vendorId === vendorId);
  };

  return (
    <div className={styles.dashboardContainer}>
      <div className={styles.header}>
        <h2 className={styles.dashboardTitle}>Vendors</h2>
        <button
          className={styles.addButton}
          onClick={() => setShowAdd(true)}
        >
          + Add Vendor
        </button>
      </div>

      <div className={styles.statsContainer}>
        <div className={styles.statCard}>
          <div className={styles.statNumber}>{vendors.length}</div>
          <div className={styles.statLabel}>Total Vendors</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statNumber}>{items.length}</div>
          <div className={styles.statLabel}>Total Items</div>
        </div>
      </div>

      <div className={styles.vendorsGrid}>
        {vendors.map(vendor => {
          const vendorItems = getVendorItems(vendor.id);
          const itemCount = vendorItems.length;
          
          return (
            <div 
              key={vendor.id} 
              id={`vendor-${vendor.id}`}
              className={styles.vendorCard}
            >
              <div className={styles.vendorHeader}>
                <div className={styles.vendorIcon}>🏢</div>
                <div className={styles.vendorName}>{vendor.name}</div>
              </div>
              
              <div className={styles.vendorDetails}>
                {vendor.contact && (
                  <div className={styles.vendorContact}>
                    <span className={styles.contactLabel}>Contact:</span> {vendor.contact}
                  </div>
                )}
                {vendor.email && (
                  <div className={styles.vendorEmail}>
                    <span className={styles.contactLabel}>Email:</span> 
                    <a href={`mailto:${vendor.email}`} className={styles.emailLink}>
                      {vendor.email}
                    </a>
                  </div>
                )}
                {vendor.phone && (
                  <div className={styles.vendorPhone}>
                    <span className={styles.contactLabel}>Phone:</span> 
                    <a href={`tel:${vendor.phone}`} className={styles.phoneLink}>
                      {vendor.phone}
                    </a>
                  </div>
                )}
                {vendor.address && (
                  <div className={styles.vendorAddress}>
                    <span className={styles.contactLabel}>Address:</span> {vendor.address}
                  </div>
                )}
                {vendor.website && (
                  <div className={styles.vendorWebsite}>
                    <span className={styles.contactLabel}>Website:</span> 
                    <a 
                      href={vendor.website.startsWith('http') ? vendor.website : `https://${vendor.website}`} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className={styles.websiteLink}
                    >
                      {vendor.website}
                    </a>
                  </div>
                )}
                
                <div className={styles.itemsInfo}>
                  <div className={styles.itemCount}>
                    {itemCount} {itemCount === 1 ? 'item' : 'items'}
                  </div>
                  {itemCount > 0 && (
                    <div className={styles.itemsList}>
                      {vendorItems.slice(0, 3).map(item => (
                        <span key={item.id} className={styles.itemName}>
                          {item.name}
                        </span>
                      ))}
                      {itemCount > 3 && (
                        <span className={styles.moreItems}>
                          +{itemCount - 3} more
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className={styles.vendorActions}>
                <button
                  className={styles.editButton}
                  onClick={() => handleEdit(vendor)}
                >
                  Edit
                </button>
                <button
                  className={styles.deleteButton}
                  onClick={() => handleDelete(vendor.id)}
                >
                  Delete
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {vendors.length === 0 && (
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}>🏢</div>
          <div className={styles.emptyText}>No vendors found</div>
          <div className={styles.emptySubtext}>Add your first vendor to get started</div>
        </div>
      )}

      {/* Add/Edit Modal */}
      {(showAdd || showEdit) && (
        <div className={styles.modal}>
          <div className={styles.modalContent}>
            <h3>{showEdit ? "Edit Vendor" : "Add Vendor"}</h3>
            <form onSubmit={showEdit ? handleEditSubmit : handleSubmit}>
              <div className={styles.formGroup}>
                <label>Vendor Name *</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g., ABC Packaging Supplies"
                  required
                />
              </div>

              <div className={styles.formGroup}>
                <label>Contact Person</label>
                <input
                  type="text"
                  value={form.contact}
                  onChange={(e) => setForm({ ...form, contact: e.target.value })}
                  placeholder="e.g., John Smith"
                />
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label>Email</label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="contact@vendor.com"
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>Phone</label>
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    placeholder="+1 (555) 123-4567"
                  />
                </div>
              </div>

              <div className={styles.formGroup}>
                <label>Address</label>
                <textarea
                  value={form.address}
                  onChange={(e) => setForm({ ...form, address: e.target.value })}
                  placeholder="Street address, city, state, zip code"
                  rows="3"
                />
              </div>

              <div className={styles.formGroup}>
                <label>Website</label>
                <input
                  type="url"
                  value={form.website}
                  onChange={(e) => setForm({ ...form, website: e.target.value })}
                  placeholder="www.vendor.com"
                />
              </div>
              
              <div className={styles.modalActions}>
                <button
                  type="button"
                  onClick={() => {
                    setShowAdd(false);
                    setShowEdit(false);
                    setForm({ name: "", contact: "", email: "", phone: "", address: "", website: "" });
                    setEditVendor(null);
                  }}
                  className={styles.cancelButton}
                >
                  Cancel
                </button>
                <button type="submit" className={styles.saveButton}>
                  {showEdit ? "Update" : "Save"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
